/**
 * The loaded scenes and image sources the Pictures view and the handoff show
 * (spec 026). Scenes come through the preview's loader (009: the open
 * scene's live state, else the draft cache, else `GET /scenes/:id`) and are
 * re-read when a listing row changes; images decode once per media id.
 */
import { useEffect, useMemo, useState } from 'react';
import type { StoryApiResponse } from '../../../../Shared/src/@types';
import { apiClient } from '../../api/client';
import { loadPreviewImage, onPreviewImageChanged } from '../../utils/previewMedia';
import { loadPreviewScene } from '../../utils/previewSources';
import { sceneKeyOf, sceneRowName, storySceneOrder, type PictureSceneRow } from './picturesModel';

/**
 * The story's scenes in story order, each as soon as it loads (a row that
 * fails to load keeps an empty document). A change of `reloadKey` (the
 * Pictures view passes the story's picture map, a new object after every
 * applied or undone batch) re-reads them, so values the map does not carry,
 * such as a state's playback (spec 035), show the scenes as they are now.
 */
export function usePictureScenes(story: Pick<StoryApiResponse, 'graph' | 'storyScenes'>, reloadKey?: unknown): PictureSceneRow[] {
  const [docs, setDocs] = useState<Record<number, PictureSceneRow['doc'] | null>>({});
  const rowsKey = story.storyScenes.map((ss) => `${ss.id}:${ss.sceneId}:${ss.updatedAt}:${ss.sceneName ?? ''}`).join('|');
  useEffect(() => {
    let cancelled = false;
    for (const ss of story.storyScenes) {
      void loadPreviewScene(ss.sceneId).then((scene) => {
        if (cancelled) return;
        setDocs((prev) => ({ ...prev, [ss.sceneId]: (scene as unknown as PictureSceneRow['doc']) ?? null }));
      });
    }
    return () => { cancelled = true; };
    // rowsKey stands for the listing rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey, reloadKey]);

  return useMemo(() => storySceneOrder(story).map((ss): PictureSceneRow => {
    const name = sceneRowName(ss);
    const doc = docs[ss.sceneId] ?? { name, sprites: [], dialogs: [] };
    return { key: sceneKeyOf(ss.id), name, storySceneId: ss.id, sceneId: ss.sceneId, doc: { ...doc, name: doc.name || name } };
  }), [story, docs]);
}

/** Object URLs of decoded images, by media id. */
export function useMediaSrcs(mediaIds: readonly number[]): (mediaId: number) => string | undefined {
  const [srcs, setSrcs] = useState<Record<number, string>>({});
  const ids = [...new Set(mediaIds.filter((id) => id > 0))];
  const key = ids.join(',');
  useEffect(() => {
    let cancelled = false;
    for (const id of ids) {
      if (srcs[id]) continue;
      void loadPreviewImage(apiClient, id).then((img) => {
        if (!cancelled && img?.src) setSrcs((prev) => (prev[id] ? prev : { ...prev, [id]: img.src }));
      });
    }
    // A picture redrawn on the server (spec 027) shows without a reload.
    const off = onPreviewImageChanged((id) => {
      void loadPreviewImage(apiClient, id).then((img) => {
        if (!cancelled && img?.src) setSrcs((prev) => ({ ...prev, [id]: img.src }));
      });
    });
    return () => { cancelled = true; off(); };
    // key stands for the ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (id: number) => srcs[id];
}
